/**
 * Who made a template and when it last changed, for the template list and card.
 *
 * GET /templates returns `author_id` (the creator; the last saver on rows older
 * than the column) and `updated_at` (ISO, or null on rows not saved since the
 * column was added). Names are not stored: they are read from monday by id, so
 * a renamed user shows their current name.
 */
import { describeApiErrors } from './useBoardColumns';

export const USERS_BY_ID_QUERY = 'query ($ids: [ID!]) { users (ids: $ids) { id name } }';

/** The distinct author ids across `templates`, as strings. */
export function authorIds(templates) {
  const ids = new Set();
  for (const t of templates || []) if (t && t.author_id != null) ids.add(String(t.author_id));
  return [...ids];
}

/**
 * { [id]: name } for `ids`, or {} when monday refuses (needs users:read). Never
 * rejects: a missing author is a missing label, not a broken list.
 */
export async function fetchUserNames(api, ids) {
  if (!ids || !ids.length) return {};
  try {
    const res = await api.api(USERS_BY_ID_QUERY, { variables: { ids } });
    const error = describeApiErrors(res);
    if (error) {
      console.error('Could not read the template authors:', error);
      return {};
    }
    const out = {};
    for (const u of (res && res.data && res.data.users) || []) out[String(u.id)] = u.name;
    return out;
  } catch (e) {
    console.error('Could not read the template authors', e);
    return {};
  }
}

/** The author's name, or null when unknown (no id, or monday did not return it). */
export function authorNameOf(template, names) {
  if (!template || template.author_id == null) return null;
  return (names && names[String(template.author_id)]) || null;
}

/** "Oct 16, 2026, 3:04 PM" in the viewer's locale and timezone; null for no or a bad date. */
export function formatUpdatedAt(iso, locale) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(d);
}
